import {helpCenterBaseUrl} from 'locales/utilities';

import {isAbsolute} from './utilities';

export interface QueryParams {
  [key: string]: any;
}

interface HelpCenterParams extends QueryParams {
  locale: string;
  query?: string;
  path?: string;
}

const ROOT_URL = '/admin';
const APP_REDIRECT_REGEX = new RegExp(
  `^${ROOT_URL}/api_permissions/\\d+/redirect$`,
);

export class Url {
  constructor(
    public hostname: string,
    public webHost = 'web.myshopify.io',
    public coreCdnHost = 'cdn.myshopify.io',
    public coreHost = 'app.myshopify.io',
    public adminDomain = 'myshopify.io',
  ) {}

  appOrigin() {
    return `https://${this.webHost}`;
  }

  home(params?: QueryParams) {
    return withQueryParams(ROOT_URL, params);
  }

  login(params?: QueryParams) {
    return withQueryParams(`${ROOT_URL}/auth/login`, params);
  }

  isExternal(url: string, hostname: string = this.hostname) {
    const absolute = isAbsolute(url);

    if (absolute) {
      let parsedUrl;

      try {
        parsedUrl = new URL(url);
      } catch (error) {
        // Invalid URL
        return true;
      }

      return (
        parsedUrl.hostname !== hostname ||
        !parsedUrl.pathname.includes('/admin')
      );
    }

    return (
      (url.startsWith('/') && !url.startsWith(ROOT_URL)) ||
      APP_REDIRECT_REGEX.test(url)
    );
  }

  makeAbsolute(path: string) {
    return isAbsolute(path) ? path : `https://${this.hostname}${path}`;
  }

  helpCenterUrl({locale, path = '', ...params}: HelpCenterParams) {
    const basePath = helpCenterBaseUrl(locale);
    if (!path) {
      return withQueryParams(basePath, params);
    }

    return withQueryParams(`${basePath}/${path}`, params);
  }

  helpCenterSearch({query, locale, ...params}: HelpCenterParams) {
    return this.helpCenterUrl({
      locale,
      path: 'search',
      q: query,
      ...params,
    });
  }

  shopifyTermsOfService(language: string, fragment?: string) {
    switch (language) {
      case 'de':
        return withFragment('https://www.shopify.de/legal/agb', fragment);
      case 'es':
        return withFragment('https://es.shopify.com/legal/terminos', fragment);
      case 'fr':
        return withFragment(
          'https://fr.shopify.com/legal/conditions',
          fragment,
        );
      case 'it':
        return withFragment('https://it.shopify.com/legal/termini', fragment);
      case 'nl':
        return withFragment(
          'https://www.shopify.nl/juridisch/voorwaarden',
          fragment,
        );
      case 'pt-BR':
        return withFragment('https://pt.shopify.com/legal/termos', fragment);
      default:
        return withFragment('https://www.shopify.com/legal/terms', fragment);
    }
  }

  shopifyPrivacyPolicy(language: string) {
    switch (language) {
      case 'de':
        return 'https://www.shopify.de/legal/datenschutz';
      case 'es':
        return 'https://es.shopify.com/legal/privacidad';
      case 'fr':
        return 'https://fr.shopify.com/legal/confidentialite';
      case 'it':
        return 'https://it.shopify.com/legal/privacy';
      case 'nl':
        return 'https://www.shopify.nl/juridisch/privacy';
      case 'pt-BR':
        return 'https://pt.shopify.com/legal/privacidade';
      default:
        return 'https://www.shopify.com/legal/privacy';
    }
  }

  translateAndAdaptReviewUrl(locale: string) {
    return `https://apps.shopify.com/translate-and-adapt?locale=${locale}#modal-show=ReviewListingModal`;
  }
}

/**
 * Appends query parameters onto a base URL, if provided.
 *
 * Parameters are appended like:
 *
 *    Simple params
 one: key=value
 many: key=value&key=value
 e.g., {id: 2, orders_placed: 2} => id=2&orders_placed=2
 *
 *     Array params
 e.g., { product_id: [1,2,3] => product_id[]=1,2,3
 *
 *     Nested params
        one: key[nestedKey]=nestedValue
        many: key[nestedKey]=nestedValue&key[nestedKey]=nestedValue
      e.g., { draft_order: { id: 2, orders_placed: 2 } } =>
            draft_order[id]=2&draft_order[orders_placed]=2
 *
 *     Deeply nested params
        one: key[nestedKey]=nestedValue
        many: key[nestedKey]=nestedValue&key[nestedKey]=nestedValue
      e.g., { draft_order: { customer: { id: 2}, orders_placed: 2 } } =>
            draft_order[customer][id]=2&draft_order[orders_placed]=2
 *
 * This function does _not_ encode URI components for query parameters by default, as others have come to rely upon the fact that it does not do so in their `uri.someroute(...)` calls.  If it did so, it would double URI encode,leading to other bugs.  We've added a mechanism to URI encode, but it must be opted into for each call.  This decision was made to limit blast radius of URL changes during BFCM, as codepoints relying on busted behaviour may be impossible to fully track down. More info: https://github.com/Shopify/web/issues/12529
 *
 * @param url The base URL
 * @param params Query parameters
 * @param shouldEncodeURIComponents Whether the supplied query parameters should be URI-encoded by this function
 */
export function withQueryParams(
  url: string,
  params?: QueryParams,
  shouldEncodeURIComponents = false,
) {
  if (!params || !Object.keys(params).length) {
    return url;
  }

  const parsedParams = Object.entries(params)
    .map(([currentKey, currentValue]) => {
      return parseQueryParams(
        currentKey,
        currentValue,
        shouldEncodeURIComponents,
      );
    })
    .join('&');

  return `${url}?${parsedParams}`;
}

/**
 * Encodes query parameter key value pairs into URL query string format. Supports array and nested object value types.
 *
 * @param key The query param key
 * @param value The query param value
 * @param shouldEncode Whether or not to perform URI encoding on the `value`
 */

export function parseQueryParams(
  key: string,
  value: string | QueryParams,
  shouldEncode: boolean,
): string {
  if (Array.isArray(value)) {
    return `${key}[]=${handleURIEncoding(value.join(','), shouldEncode)}`;
  } else if (typeof value === 'object') {
    return Object.entries(value as QueryParams)
      .map(([nestedKey, nestedValue]) => {
        return typeof nestedValue === 'object'
          ? `${key}${parseQueryParams(
              `[${nestedKey}]`,
              nestedValue,
              shouldEncode,
            )}`
          : `${key}[${nestedKey}]=${handleURIEncoding(
              nestedValue,
              shouldEncode,
            )}`;
      })
      .join('&');
  }

  return `${key}=${handleURIEncoding(value, shouldEncode)}`;
}

/**
 * URI encodes the supplied `value` if `shouldEncode` is true.
 *
 * @param value The query param value
 * @param shouldEncode Whether or not to URI encode the supplied value
 */

function handleURIEncoding(value: string, shouldEncode: boolean) {
  return shouldEncode ? encodeURIComponent(value) : value;
}

/**
 * Appends a fragment to a URL if provided.
 *
 * @param url The base URL
 * @param fragment A fragment value to be appended
 */
export function withFragment(url: string, fragment?: string): string {
  if (fragment) {
    return `${url}#${fragment}`;
  }

  return url;
}
